import {dehydrate, QueryClient} from '@tanstack/react-query';
import {GetStaticPathsContext, GetStaticPropsContext, GetStaticPropsResult} from 'next';

import {RENDER_MODES} from '@udemy/nextjs-core';
import {udSentry} from '@udemy/sentry';

import {CategoryPage} from 'src/features/category';
import {CategoryPageProps} from 'src/features/category/types';
import {getCompactTranslations} from 'src/lib-server/locale.server';
import {FOUR_HOURS} from 'src/lib/constants';
import {logger} from 'src/lib/logger';
import {getCategoryData, shouldRedirect} from 'src/pages/courses/lib/get-category-data';
import {AppContextData} from 'src/types/page-props';

import {getStaticAppContext} from '../api/context.api';
import {CategoryNotFoundError, CategoryValidationError} from './lib/errors';

export async function getStaticPaths({}: GetStaticPathsContext) {
    return {
        paths: [],
        fallback: 'blocking',
    };
}
export async function getStaticProps({
    locale,
    params,
}: GetStaticPropsContext): Promise<GetStaticPropsResult<CategoryPageProps>> {
    locale = locale as string;
    // Common page data
    const translations = await getCompactTranslations(locale);
    const client = new QueryClient();
    const categorySlug = params?.category as string;

    try {
        if (!categorySlug) {
            throw new CategoryValidationError(
                `Category slug has not been provided. Slug: ${categorySlug}`,
            );
        }

        if (categorySlug === 'featured') {
            return {
                redirect: {
                    destination: '/',
                    statusCode: 301,
                },
            };
        }

        // Resolve appContext
        const [staticData] = await Promise.all([getStaticAppContext({locale})]);
        const appContext: AppContextData = {
            ...staticData,
            // application specific context can go here
        };

        // Category data
        const categoryData = await getCategoryData(categorySlug, locale);

        if (shouldRedirect(categoryData)) {
            const redirectContext = categoryData.redirect_context;
            const redirectionPath = redirectContext.destination;

            logger.log({
                level: 'info',
                message: 'Redirection will happen for',
                categorySlug,
                redirectContext,
            });

            /**
             * Normally, next.js returns 308 when request is permanently moved. But in udemy, we've different status
             * codes and for the topics we're using 301.
             *
             * But somehow, next.js caches this 308 responses and returns the same page for the next request.
             * TODO: GET THE REQUEST FROM THE API IT'S AVAILABLE, THIS IS JUST FOR TEST PURPOSES
             */
            return {
                redirect: redirectContext.permanent
                    ? {destination: redirectionPath, statusCode: 301}
                    : {destination: redirectionPath, permanent: false},
            };
        }

        return {
            props: {
                appContext,
                dehydratedState: dehydrate(client),
                locale,
                pageKey: 'discovery_category',
                pageObject: categoryData.pageObject,
                renderMode: RENDER_MODES.ISR as keyof typeof RENDER_MODES, // supports render functions in _document.tsx and _app.tsx.
                staticDiscoveryUnits: [],
                translations,
                ...(categoryData.metaProps ? {metaProps: categoryData.metaProps} : {}),
                ...(categoryData.category ? {staticCategory: categoryData.category} : {}),
            },
            revalidate: FOUR_HOURS,
        };
    } catch (error) {
        udSentry.captureException(error);
        // eslint-disable-next-line no-console
        console.error('unknown exception on category page', error);

        if (error instanceof CategoryValidationError || error instanceof CategoryNotFoundError) {
            return {
                notFound: true,
            };
        }
        throw error;
    }
}

export default CategoryPage;
